import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import api from "@/lib/api";
import { ArrowLeft, Loader2, Download, ZoomIn, ZoomOut, RefreshCw } from "lucide-react";

/**
 * Read-only safety-sheet viewer for the public QR page.
 *
 * Renders the manually uploaded safety-sheet PDF with react-pdf (pdf.js).
 * The file is fetched ONCE by <Document>; zooming only re-renders the page
 * canvases (no re-fetch, no gview/iframe, no cross-origin translate issues).
 * A prominent "Scarica PDF" button is shown when the admin enabled
 * `allow_download` on the QR layout "safety_sheet" block.
 */

// Serve the pdf.js worker from /public so it resolves in BOTH the dev server
// and the static production build (import.meta.url asset emit breaks on deploy).
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;

const L = {
  it: { title: "Scheda di sicurezza", back: "Torna al prodotto", noFile: "Scheda non disponibile.", packagingUnavailable: "Documento non disponibile.", loading: "Caricamento...", download: "Scarica PDF", err: "Impossibile visualizzare il PDF.", retry: "Riprova", page: "Pagina" },
  en: { title: "Safety data sheet", back: "Back to product", noFile: "Document not available.", packagingUnavailable: "Document not available.", loading: "Loading...", download: "Download PDF", err: "Unable to display the PDF.", retry: "Retry", page: "Page" },
  fr: { title: "Fiche de sécurité", back: "Retour au produit", noFile: "Document indisponible.", packagingUnavailable: "Document indisponible.", loading: "Chargement...", download: "Télécharger le PDF", err: "Impossible d'afficher le PDF.", retry: "Réessayer", page: "Page" },
  es: { title: "Ficha de seguridad", back: "Volver al producto", noFile: "Documento no disponible.", packagingUnavailable: "Documento no disponible.", loading: "Cargando...", download: "Descargar PDF", err: "No se puede mostrar el PDF.", retry: "Reintentar", page: "Página" },
  de: { title: "Sicherheitsdatenblatt", back: "Zurück zum Produkt", noFile: "Dokument nicht verfügbar.", packagingUnavailable: "Dokument nicht verfügbar.", loading: "Laden...", download: "PDF herunterladen", err: "PDF kann nicht angezeigt werden.", retry: "Erneut versuchen", page: "Seite" },
};

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

export default function PublicSafetySheet({ variant = "safety" }) {
  const { slug, documentId } = useParams();
  const isPackaging = variant === "packaging";
  const testPrefix = isPackaging ? "packaging" : "safety";
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pdfError, setPdfError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [baseWidth, setBaseWidth] = useState(800);
  const containerRef = useRef(null);

  const browserLang = (typeof navigator !== "undefined" && navigator.language?.slice(0, 2)) || "it";
  const t = L[browserLang] || L.it;

  useEffect(() => {
    document.title = "Cosmely";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    api.get(`/public/products/${slug}`)
      .then(r => {
        if (r.data?.disabled) { nav("/disabled"); return; }
        setData(r.data);
      })
      .catch(() => setError("not-found"));
  }, [slug, nav]);

  // Fit-to-width: track the scroll container width (responsive desktop/mobile).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      // Leave a little breathing room; cap so it doesn't get huge on desktop.
      setBaseWidth(Math.max(280, Math.min(w - 24, 900)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  const selectedDocument = isPackaging
    ? data?.packaging_certifications?.find(doc => doc.id === documentId)
    : null;
  const sourceUrl = isPackaging ? selectedDocument?.url : data?.safety_sheet_url;
  const fileUrl = sourceUrl ? `${process.env.REACT_APP_BACKEND_URL}${sourceUrl}` : null;

  // Memoise so <Document> doesn't re-fetch on every re-render (zoom/scroll).
  const file = useMemo(() => (fileUrl ? { url: fileUrl } : null), [fileUrl]);
  const docOptions = useMemo(() => ({ withCredentials: false }), []);

  const onLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPdfError(false);
  }, []);
  const onLoadError = useCallback((err) => { console.warn("[public-pdf] load error", err); setPdfError(true); }, []);

  const retry = () => { setPdfError(false); setNumPages(0); setReloadKey(k => k + 1); };
  const zoomIn = () => setZoom(z => Math.min(MAX_ZOOM, +(z + 0.25).toFixed(2)));
  const zoomOut = () => setZoom(z => Math.max(MIN_ZOOM, +(z - 0.25).toFixed(2)));

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] p-8 text-center">
        <div>
          <div className="font-serif text-3xl mb-2">{isPackaging ? t.packagingUnavailable : t.noFile}</div>
          <button onClick={() => nav(`/p/${slug}`)} className="btn-ghost mt-4" data-testid={`${testPrefix}-back-error`}>{t.back}</button>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <Loader2 className="w-6 h-6 animate-spin text-[#C5B39B]" />
      </div>
    );
  }
  if (!sourceUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] p-8 text-center">
        <div>
          <div className="font-serif text-3xl mb-2" data-testid={`${testPrefix}-unavailable`}>
            {isPackaging ? t.packagingUnavailable : t.noFile}
          </div>
          <button onClick={() => nav(`/p/${slug}`)} className="btn-ghost mt-4" data-testid={`${testPrefix}-back-empty`}>
            <ArrowLeft className="w-3 h-3 inline mr-2" />{t.back}
          </button>
        </div>
      </div>
    );
  }

  const allowDownload = isPackaging
    ? !!data.packaging_certification_allow_download : !!data.safety_sheet_allow_download;
  const downloadHref = `${fileUrl}?download=1`;
  const downloadName = isPackaging
    ? `${selectedDocument.name.replace(/[\\/:*?"<>|]/g, "-")}.pdf`
    : `scheda-sicurezza-${(data.code || slug).replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`;
  const pageWidth = Math.round(baseWidth * zoom);

  return (
    <div className="public-page h-screen flex flex-col bg-[#525659]" data-testid={`public-${testPrefix}`}>
      <header className="shrink-0 backdrop-blur bg-[#FAF9F6]/95 border-b border-[#E5E5E0]">
        <div className="flex justify-between items-center px-4 sm:px-6 py-3 max-w-4xl mx-auto gap-3">
          <button onClick={() => nav(`/p/${slug}`)}
                  className="text-xs tracking-luxury uppercase text-[#1A1A1A] hover:text-[#707070] flex items-center shrink-0"
                  data-testid={`${testPrefix}-back-btn`}>
            <ArrowLeft className="w-3 h-3 mr-2" strokeWidth={1.5} />
            <span className="hidden sm:inline">{t.back}</span>
          </button>
          {isPackaging && (
            <span className="min-w-0 flex-1 truncate text-center text-xs text-[#1A1A1A]"
                  data-testid="packaging-viewer-document-name" title={selectedDocument.name}>
              {selectedDocument.name}
            </span>
          )}

          <div className="flex items-center gap-2">
            <button onClick={zoomOut} disabled={zoom <= MIN_ZOOM}
                    className="w-8 h-8 flex items-center justify-center border border-[#1A1A1A] text-[#1A1A1A] disabled:opacity-30"
                    aria-label="Zoom out" data-testid={`${testPrefix}-zoom-out`}>
              <ZoomOut className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
            <span className="text-[11px] tabular-nums text-[#707070] w-10 text-center" data-testid={`${testPrefix}-zoom-level`}>
              {Math.round(zoom * 100)}%
            </span>
            <button onClick={zoomIn} disabled={zoom >= MAX_ZOOM}
                    className="w-8 h-8 flex items-center justify-center border border-[#1A1A1A] text-[#1A1A1A] disabled:opacity-30"
                    aria-label="Zoom in" data-testid={`${testPrefix}-zoom-in`}>
              <ZoomIn className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
            {allowDownload && (
              <a href={downloadHref} download={downloadName} target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-1.5 text-xs tracking-luxury uppercase bg-[#1A1A1A] text-[#FAF9F6] px-3 py-1.5 ml-1 hover:bg-[#2A2A2A] transition-colors"
                 data-testid={`${testPrefix}-download-btn`}>
                <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="hidden sm:inline">{t.download}</span>
              </a>
            )}
          </div>
        </div>
      </header>

      <div ref={containerRef} className="flex-1 overflow-auto py-4 px-3"
           style={{ WebkitOverflowScrolling: "touch" }} data-testid={`${testPrefix}-pdf-viewer`}>
        {pdfError ? (
          <div className="h-full flex items-center justify-center text-center">
            <div className="bg-white rounded-sm p-8 max-w-sm">
              <div className="font-serif text-2xl mb-2 text-[#1A1A1A]">{t.err}</div>
              <div className="flex items-center justify-center gap-3 mt-4">
                <button onClick={retry} className="btn-ghost inline-flex items-center gap-2" data-testid={`${testPrefix}-retry-btn`}>
                  <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />{t.retry}
                </button>
                {allowDownload && (
                  <a href={downloadHref} download={downloadName} target="_blank" rel="noopener noreferrer"
                     className="btn-primary inline-flex items-center gap-2" data-testid={`${testPrefix}-download-error`}>
                    <Download className="w-3.5 h-3.5" strokeWidth={1.5} />{t.download}
                  </a>
                )}
              </div>
            </div>
          </div>
        ) : (
          <Document
            key={reloadKey}
            file={file}
            options={docOptions}
            onLoadSuccess={onLoadSuccess}
            onLoadError={onLoadError}
            loading={
              <div className="h-full flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-[#C5B39B]" />
              </div>
            }
            className="flex flex-col items-center gap-4"
          >
            {Array.from({ length: numPages }, (_, i) => (
              <div key={i} className="shadow-lg bg-white" data-testid={`${testPrefix}-page-${i + 1}`}>
                <Page
                  pageNumber={i + 1}
                  width={pageWidth}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  loading={
                    <div style={{ width: pageWidth, height: pageWidth * 1.414 }}
                         className="flex items-center justify-center bg-white">
                      <Loader2 className="w-5 h-5 animate-spin text-[#C5B39B]" />
                    </div>
                  }
                />
              </div>
            ))}
          </Document>
        )}
      </div>

      {/* Prominent download fallback below the viewer (shown when enabled). */}
      {allowDownload && (
        <div className="shrink-0 bg-[#FAF9F6] border-t border-[#E5E5E0] px-4 py-3 flex justify-center">
          <a href={downloadHref} download={downloadName} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FAF9F6] px-5 py-2.5 rounded-sm text-sm tracking-wide hover:bg-[#2A2A2A] transition-colors"
             data-testid={`${testPrefix}-download-bottom`}>
            <Download className="w-4 h-4" strokeWidth={1.5} />{t.download}
          </a>
        </div>
      )}
    </div>
  );
}
